import { useRef, useState, useCallback, useEffect } from "react";
import { ChevronsLeftRight } from "lucide-react";
import { ASSETS } from "@/lib/assets";
import { Reveal } from "@/components/site/Reveal";

export const ResultsSlider = () => {
  const containerRef = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const setFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(100, Math.max(0, p)));
  }, []);

  useEffect(() => {
    const move = (e) => {
      if (!dragging.current) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      setFromClientX(clientX);
    };
    const up = () => (dragging.current = false);
    window.addEventListener("mousemove", move);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchend", up);
    };
  }, [setFromClientX]);

  const start = (e) => {
    dragging.current = true;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    setFromClientX(clientX);
  };

  return (
    <section id="results" className="relative py-28 md:py-40 scroll-mt-24">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
          <Reveal className="md:col-span-7">
            <p className="overline mb-5">Proof</p>
            <h2 className="font-serif font-light text-5xl md:text-7xl leading-[0.95] tracking-[-0.02em]">
              Skincare that<br />proves itself.
            </h2>
          </Reveal>
          <Reveal className="md:col-span-5" delay={0.1}>
            <p className="text-base text-muted-foreground leading-relaxed">
              Because ASYA measures every session, progress is visible — not promised. Drag to see a typical transformation across a treatment cycle.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div
            ref={containerRef}
            data-testid="results-slider"
            className="relative w-full aspect-[16/10] md:aspect-[21/9] rounded-2xl overflow-hidden border border-white/10 select-none cursor-ew-resize"
            onMouseDown={start}
            onTouchStart={start}
          >
            {/* After (base) */}
            <img src={ASSETS.after} alt="Skin after ASYA" className="absolute inset-0 w-full h-full object-cover" draggable={false} />
            <span className="absolute bottom-4 right-5 font-mono text-[10px] uppercase tracking-[0.25em] text-white/90 drop-shadow">
              After
            </span>

            {/* Before (clipped from the right via clip-path) */}
            <img
              src={ASSETS.before}
              alt="Skin before ASYA"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
              draggable={false}
            />
            <span className="absolute bottom-4 left-5 font-mono text-[10px] uppercase tracking-[0.25em] text-white/90 drop-shadow">
              Before
            </span>

            {/* Handle */}
            <div className="absolute top-0 bottom-0 z-10" style={{ left: `calc(${pos}% - 1px)` }}>
              <div className="w-0.5 h-full bg-white/90" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full glass border border-white/40 flex items-center justify-center">
                <ChevronsLeftRight className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="grid grid-cols-3 gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/10 mt-6">
            {[["+31%", "Hydration"], ["−24%", "Visible redness"], ["82", "Avg. Skin Index"]].map(([v, l]) => (
              <div key={l} className="bg-background p-6 md:p-8 text-center">
                <div className="font-serif text-4xl md:text-5xl text-primary">{v}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-2">{l}</div>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
